import React, { useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { format } from 'date-fns';
import apiService from '../services/apiService';
import { toast } from 'react-hot-toast';
import { Clock, Phone, ChevronLeft, ChevronRight } from 'lucide-react';
import { useBranding } from '../context/BrandingContext';
import './PublicCalendarWidget.css';

// [260928-v4n] Статус — только из поля booking ответа (бэкенд ставит его на каждом пути).
// Отсутствует/неизвестен → 'request_only': «забронировано» без подтверждения сервера не говорим.
const IZVESTNYE = ['scheduled', 'request_only', 'failed'];
export const statusIzOtveta = (otvet) => {
    const s = otvet && otvet.booking && otvet.booking.status;
    return IZVESTNYE.includes(s) ? s : 'request_only';
};

// Строка времени → Date; пусто или не разбирается → null.
const dataIzStroki = (s) => {
    if (!s) return null;
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? null : d;
};

const PublicCalendarWidget = () => {
    // eslint-disable-next-line no-unused-vars
    const { branding, isFitenko } = useBranding();
    // [PROPOSED 09-07] Адрес встречи — из данных арендатора (/api/v1/branding → company_profiles.address),
    // не литералом: виджет общий для всех арендаторов. Пусто → вариант без адреса.
    const adresOfisa = (branding && typeof branding.address === 'string' && branding.address.trim()) || null;
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [slots, setSlots] = useState([]);
    const [loading, setLoading] = useState(false);
    const [booking, setBooking] = useState(null); // Selected slot to book
    const [formData, setFormData] = useState({
        fullName: '',
        email: '',
        phone: '',
        topic: 'consultation',
        consultationType: 'Phone', // [NEW] Default
        city: '', // [NEW]
        issueDescription: '' // [NEW]
    });
    const [step, setStep] = useState('date'); // 'date' | 'form' | 'success'
    // [260928-v4n] Что сервер сделал с временем: 'scheduled' | 'request_only' | 'failed'; и время события.
    const [statusBroni, setStatusBroni] = useState('request_only');
    const [nachaloBroni, setNachaloBroni] = useState(null);

    const fetchSlots = async (date) => {
        setLoading(true);
        try {
            const dateStr = format(date, 'yyyy-MM-dd');
            const freeSlots = await apiService.getFreeSlots(dateStr);
            setSlots(freeSlots);
        } catch (error) {
            console.error("Failed to fetch slots:", error);
            // toast.error("Could not load available times.");
        } finally {
            setLoading(false);
        }
    };

    // Initial fetch when date changes
    React.useEffect(() => {
        if (selectedDate) {
            fetchSlots(selectedDate);
        }
    }, [selectedDate]);

    // [New] Theming Support (Wave 3 — RESEARCH Pitfall 5 gate)
    // The widget supports an embeddable `?primaryColor=ff0000` query-param override
    // for 3rd-party hosts. On tenant-themed deployments (Fitenko OS), we must NOT
    // let an absent query param clobber the tenant palette via setProperty(undefined).
    // Gate: only apply override when primaryColor is explicitly provided AND the
    // document is not already Fitenko-themed (Fitenko tenant theming takes precedence).
    React.useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const primaryColor = params.get('primaryColor');
        const isFitenkoTenant = document.documentElement.classList.contains('fitenko');
        if (primaryColor && !isFitenkoTenant) {
            document.documentElement.style.setProperty('--primary-color', `#${primaryColor}`);
            // Also set derived colors if needed, or use CSS calc/opacity
            document.documentElement.style.setProperty('--primary-hover', `#${primaryColor}dd`);
        }
        // [NEW] Opt-in dark theme for embeds hosted on dark-themed sites (e.g. estatelawbrooklyn.com).
        // Toggled via ?theme=dark; styling lives in the `html.embed-dark` block of the CSS.
        // Additive + gated → MLG/Fitenko embeds (no theme param) keep their light palette.
        if (params.get('theme') === 'dark') {
            document.documentElement.classList.add('embed-dark');
        }
    }, []);

    const handleSlotClick = (slot) => {
        setBooking(slot);
        setStep('form');
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            // Simplified Lead Creation (backend handles booking ideally, or we note it)
            // [NEW] Construct detailed notes from new fields
            const detailedNotes = `
Appointment Request: ${format(new Date(booking), 'Pp')}
Type: ${formData.consultationType} Consultation
City: ${formData.city}
Issue: ${formData.issueDescription}
            `.trim();

            const payload = {
                ...formData,
                service: 'Calendar Booking',
                // [260928-v4n] Прежний заголовок обещал бесплатную консультацию — уходил в notes лида и лгал для платной.
                topic: 'Consultation Request',
                source: 'Widget Calendar',
                language: 'en',
                appointment_datetime: booking,
                notes: detailedNotes
            };

            const otvet = await apiService.createLead(payload);
            const status = statusIzOtveta(otvet);
            setStatusBroni(status);
            setNachaloBroni(status === 'scheduled' ? ((otvet && otvet.booking && otvet.booking.start) || null) : null);
            setStep('success');

        } catch (error) {
            console.error(error);
            toast.error("Your request could not be sent. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    if (step === 'success') {
        // [260928-v4n] Три текста по статусу сервера. «Booked» — только при 'scheduled'.
        const zabronirovano = statusBroni === 'scheduled';
        const dataSobytiya = dataIzStroki(nachaloBroni) || dataIzStroki(booking);
        const dataZaprosa = dataIzStroki(booking);
        return (
            <div className="mlg-calendar-container success-view">
                <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                    {zabronirovano && <div style={{ fontSize: '48px', marginBottom: '16px' }}>🎉</div>}
                    <h3 style={{ color: 'var(--dark-blue)', marginBottom: '8px' }}>{zabronirovano ? "You're booked!" : 'Request Sent!'}</h3>
                    {zabronirovano ? (
                        <p style={{ color: 'var(--secondary-blue)', lineHeight: '1.5' }}>
                            Your consultation is booked for{' '}
                            <strong style={{ color: 'var(--primary-blue)' }}>{dataSobytiya ? format(dataSobytiya, 'PPPP p') : ''}</strong>. A confirmation will follow.
                        </p>
                    ) : (
                        <p style={{ color: 'var(--secondary-blue)', lineHeight: '1.5' }}>
                            {statusBroni === 'failed'
                                ? 'We received your request but could not reserve the time automatically — the office will contact you to confirm.'
                                : 'Thank you — the office will contact you to confirm the time.'}
                        </p>
                    )}
                    {!zabronirovano && dataZaprosa && (
                        <p style={{ color: 'var(--secondary-blue)', fontSize: '0.9rem', marginTop: '8px' }}>
                            Preferred time: <strong style={{ color: 'var(--primary-blue)' }}>{format(dataZaprosa, 'PPPP p')}</strong>
                        </p>
                    )}
                    <button onClick={() => setStep('date')} className="mlg-btn-primary" style={{ marginTop: '24px' }}>
                        Choose Another Time
                    </button>
                </div>
            </div>
        );
    }

    if (step === 'form') {
        return (
            <div className="mlg-calendar-container form-view">
                <button className="mlg-back-btn" onClick={() => setStep('date')}>
                    <ChevronLeft size={16} /> Back
                </button>

                <h3 className="mlg-section-title">Enter Details</h3>
                <p className="mlg-selected-time">
                    {format(new Date(booking), 'PPPP')} at {format(new Date(booking), 'p')}
                </p>

                <form onSubmit={handleFormSubmit} className="mlg-booking-form">
                    <div className="form-group">
                        <label>Consultation Format *</label>
                        <select
                            required
                            value={formData.consultationType}
                            onChange={e => setFormData({ ...formData, consultationType: e.target.value })}
                            style={{
                                padding: '10px 12px',
                                border: '1px solid hsl(var(--border))',
                                borderRadius: '6px',
                                fontSize: '0.95rem',
                                background: 'var(--popover, white)'
                            }}
                        >
                            <option value="In-Person">{adresOfisa ? `In-Person Consultation (${adresOfisa})` : 'In-Person Consultation'}</option>
                            <option value="Phone">Phone Consultation</option>
                            <option value="Video">Video Consultation (Zoom/Google Meet)</option>
                        </select>
                    </div>

                    <div className="form-group">
                        <label>Full Name *</label>
                        <input
                            type="text"
                            required
                            value={formData.fullName}
                            onChange={e => setFormData({ ...formData, fullName: e.target.value })}
                        />
                    </div>
                    <div className="form-group">
                        <label>Email *</label>
                        <input
                            type="email"
                            required
                            value={formData.email}
                            onChange={e => setFormData({ ...formData, email: e.target.value })}
                        />
                    </div>
                    <div className="form-group">
                        <label>Phone Number *</label>
                        <input
                            type="tel"
                            required
                            value={formData.phone}
                            onChange={e => setFormData({ ...formData, phone: e.target.value })}
                        />
                    </div>

                    <div className="form-group">
                        <label>What city do you live in? *</label>
                        <input
                            type="text"
                            required
                            placeholder="e.g. New York, Miami"
                            value={formData.city}
                            onChange={e => setFormData({ ...formData, city: e.target.value })}
                        />
                    </div>

                    <div className="form-group">
                        <label>Brief description of your legal issue *</label>
                        <textarea
                            required
                            rows={3}
                            placeholder="Please briefly describe how we can help..."
                            value={formData.issueDescription}
                            onChange={e => setFormData({ ...formData, issueDescription: e.target.value })}
                            style={{
                                padding: '10px 12px',
                                border: '1px solid hsl(var(--border))',
                                borderRadius: '6px',
                                fontSize: '0.95rem',
                                fontFamily: 'inherit',
                                resize: 'vertical'
                            }}
                        />
                    </div>

                    <button type="submit" className="mlg-btn-primary" disabled={loading}>
                        {loading ? 'Sending...' : 'Request This Time'}
                    </button>
                </form>
            </div>
        );
    }

    // [NEW] Disable weekends (Sunday=0, Saturday=6)
    const isWeekday = (date) => {
        const day = date.getDay();
        return day !== 0 && day !== 6;
    };

    return (
        <div className="mlg-calendar-container">
            {/* Header Removed as per user request */}

            {/* Calendar Section */}
            <div className="mlg-calendar-section">
                <h3 className="mlg-section-title">Select a Date & Time</h3>

                <div className="mlg-datepicker-wrapper">
                    <DatePicker
                        selected={selectedDate}
                        onChange={date => setSelectedDate(date)}
                        minDate={new Date()}
                        filterDate={isWeekday} // [NEW] Only allow Mon-Fri
                        inline
                        calendarClassName="mlg-custom-calendar"
                        dayClassName={date => "mlg-custom-day"}
                        renderCustomHeader={({
                            date,
                            decreaseMonth,
                            increaseMonth,
                            prevMonthButtonDisabled,
                            nextMonthButtonDisabled,
                        }) => (
                            <div className="mlg-calendar-nav">
                                <button onClick={decreaseMonth} disabled={prevMonthButtonDisabled}>
                                    <ChevronLeft size={20} />
                                </button>
                                <span className="current-month">
                                    {format(date, 'MMMM yyyy')}
                                </span>
                                <button onClick={increaseMonth} disabled={nextMonthButtonDisabled}>
                                    <ChevronRight size={20} />
                                </button>
                            </div>
                        )}
                    />
                </div>
            </div>

            {/* Slots Section */}
            <div className="mlg-slots-section">
                {loading ? (
                    <div className="mlg-loading">Loading slots...</div>
                ) : (
                    <div className="mlg-slots-grid">
                        {slots.length > 0 ? slots.map((slot, i) => (
                            <button
                                key={i}
                                className="mlg-slot-btn"
                                onClick={() => handleSlotClick(slot)}
                            >
                                {format(new Date(slot), 'h:mm aa')}
                            </button>
                        )) : (
                            <div className="mlg-no-slots">
                                No times available for {format(selectedDate, 'MMM d')}.
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default PublicCalendarWidget;
